import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { ThemeAvailability } from "@how/website-core";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** One theme as the Look page shows it (`list_site_themes`, ADR 0050). */
export interface SiteTheme {
  readonly key: string;
  readonly name: string;
  readonly availability: ThemeAvailability;
  /** Whether this church may use it today: included, premium on its plan, or bespoke and granted to it. */
  readonly allowed: boolean;
  /** Whether the church's site is drawn in it now. */
  readonly isCurrent: boolean;
}

/** A failed call, with the database's error code kept so the page can say what went wrong. */
export class ThemeError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message);
  }
}

function failed(error: { message: string; code?: string } | null): void {
  if (error !== null) throw new ThemeError(error.message, error.code);
}

const AVAILABILITIES: readonly ThemeAvailability[] = ["included", "premium", "bespoke"];

/** The themes this church is offered, in the catalog's order. */
export function useSiteThemes() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["site-themes", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<SiteTheme[]> => {
      const { data, error } = await supabase.rpc("list_site_themes", {
        p_tenant_id: tenantId ?? "",
      });
      failed(error);
      return (data ?? []).map((row) => ({
        key: row.key,
        name: row.name,
        // The database's check constraint allows only these three; anything else is drawn as included, never premium.
        availability: AVAILABILITIES.includes(row.availability as ThemeAvailability)
          ? (row.availability as ThemeAvailability)
          : "included",
        allowed: row.allowed,
        isCurrent: row.is_current,
      }));
    },
  });
}

/**
 * The church's public site on the platform's own address, for the theme preview frame. The platform address always
 * exists and is the one the staff dashboard's content security policy lets it frame; a custom domain may not be.
 */
export function usePlatformPublicHost() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["platform-public-host", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("tenant_domains")
        .select("hostname")
        .eq("tenant_id", tenantId ?? "")
        .eq("surface", "public_web")
        .eq("kind", "platform")
        .eq("status", "active")
        .limit(1);
      failed(error);
      return data?.[0]?.hostname ?? null;
    },
  });
}

/**
 * A 30-minute token that shows the church's home page in a theme (`create_theme_preview`). The theme is bound to the
 * token in the database; the public site never takes it from the address.
 */
export function useCreateThemePreview() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async (themeKey: string): Promise<string> => {
      const { data, error } = await supabase.rpc("create_theme_preview", {
        p_tenant_id: tenantId ?? "",
        p_theme_key: themeKey,
      });
      failed(error);
      return data as string;
    },
  });
}

/** Applies a theme for every visitor (`set_site_theme`). The rest of the brand is kept. */
export function useSetSiteTheme() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (themeKey: string): Promise<void> => {
      const { error } = await supabase.rpc("set_site_theme", {
        p_tenant_id: tenantId ?? "",
        p_theme_key: themeKey,
      });
      failed(error);
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["site-themes", tenantId] }),
        client.invalidateQueries({ queryKey: ["brand", tenantId] }),
      ]);
    },
  });
}

/** What a failed theme call means to the person who made it. */
export function themeErrorMessage(error: unknown): string {
  const code = error instanceof ThemeError ? error.code : undefined;
  if (code === "42501") return "You need permission to manage your church's settings.";
  if (code === "23514") return "Your church's plan does not include this theme.";
  if (code === "P0002") return "That theme is no longer offered.";
  return "Something went wrong. Try again.";
}
